import { useMemo, useState } from "react";
import {
    CheckIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    ShieldCheckIcon,
    Squares2X2Icon,
} from "@heroicons/react/24/outline";

import { formatPermissionCode, type PermissionCode } from "@/features/auth/permissions";

import type { PermissionCatalog, PermissionCatalogEntry } from "../types";

type PermissionCatalogEditorProps = {
    catalog: PermissionCatalog;
    selectedPermissions: PermissionCode[];
    onChange: (permissions: PermissionCode[]) => void;
    disabled?: boolean;
    hideIntro?: boolean;
    layout?: "grid" | "workspace";
};

type PermissionSummaryProps = {
    permissions: readonly string[] | null | undefined;
    catalog?: PermissionCatalog | null;
    emptyLabel?: string;
    maxVisible?: number;
};

type OrderedPermissionSection = {
    key: string;
    title: string;
    entries: PermissionCatalogEntry[];
};

const normalizeValue = (value: string) =>
    value
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

const SECTION_ORDER = [
    "Dashboard",
    "Users",
    "Riders",
    "Drivers",
    "Ride Bookings & Trips",
    "Wallet & Payments",
    "Notifications & Messaging",
    "Reports & Analytics",
    "Audit & Logs",
    "Approvals",
    "Fraud & Safety",
    "Live Monitoring",
] as const;

const SECTION_ALIASES: Record<string, string> = {
    dashboard: "Dashboard",
    users: "Users",
    riders: "Riders",
    drivers: "Drivers",
    "ride bookings trips": "Ride Bookings & Trips",
    "ride bookings and trips": "Ride Bookings & Trips",
    "ride bookings": "Ride Bookings & Trips",
    trips: "Ride Bookings & Trips",
    "wallet payments": "Wallet & Payments",
    "wallet and payments": "Wallet & Payments",
    wallets: "Wallet & Payments",
    "notifications messaging": "Notifications & Messaging",
    "notifications and messaging": "Notifications & Messaging",
    notifications: "Notifications & Messaging",
    messaging: "Notifications & Messaging",
    "reports analytics": "Reports & Analytics",
    "reports and analytics": "Reports & Analytics",
    analytics: "Reports & Analytics",
    "audit logs": "Audit & Logs",
    "audit and logs": "Audit & Logs",
    audit: "Audit & Logs",
    approvals: "Approvals",
    "fraud safety": "Fraud & Safety",
    "fraud and safety": "Fraud & Safety",
    fraud: "Fraud & Safety",
    "live monitoring": "Live Monitoring",
    live: "Live Monitoring",
};

const PERMISSION_CODE_ORDER = [
    "DASHBOARD_VIEW",
    "USERS_VIEW",
    "USERS_MAP_VIEW",
    "USERS_EXPORT",
    "USERS_MODERATE",
    "USERS_DELETE",
    "RIDERS_VIEW",
    "RIDERS_EXPORT",
    "RIDER_BOOKINGS_VIEW",
    "RIDER_TRIPS_VIEW",
    "RIDER_WALLET_VIEW",
    "DRIVERS_VIEW",
    "DRIVERS_EXPORT",
    "DRIVER_RIDES_VIEW",
    "DRIVER_TRIPS_VIEW",
    "DRIVER_WALLET_VIEW",
    "DRIVERS_MODERATE",
    "DRIVER_ROAD_WORTHINESS_REVIEW",
    "DRIVER_PAYOUTS_VIEW",
    "DRIVER_PAYOUTS_MANAGE",
    "RIDE_OFFERS_VIEW",
    "RIDE_OFFERS_MANAGE",
    "RIDE_BOOKINGS_VIEW",
    "RIDE_BOOKINGS_MANAGE",
    "RIDE_BOOKINGS_REFUND",
    "TRIPS_VIEW",
    "TRIPS_MANAGE",
    "WALLETS_VIEW",
    "WALLET_ADJUST",
    "WALLET_FREEZE",
    "TRANSACTIONS_VIEW",
    "PAYMENTS_VIEW",
    "PLATFORM_WALLET_VIEW",
    "PAYOUTS_MANAGE",
    "WITHDRAWALS_VIEW",
    "WITHDRAWALS_MANAGE",
    "WALLET_OPERATIONS_VIEW",
    "WALLET_OPERATIONS_MANAGE",
    "MESSAGING_VIEW",
    "MESSAGING_MANAGE",
    "PUSH_NOTIFICATIONS_SEND",
    "ANALYTICS_VIEW",
    "AUDIT_VIEW",
    "AUDIT_SECURITY_VIEW",
    "AUDIT_EXPORT",
    "APPROVALS_VIEW",
    "APPROVALS_EXPORT",
    "APPROVALS_MANAGE",
    "FRAUD_VIEW",
    "FRAUD_MANAGE",
    "FRAUD_RUN_JOBS",
    "LIVE_MONITORING_VIEW",
] as const;

const SECTION_ORDER_INDEX = new Map(
    SECTION_ORDER.map((section, index) => [section, index])
);

const PERMISSION_ORDER_INDEX = new Map<string, number>(
    PERMISSION_CODE_ORDER.map((permission, index) => [permission, index])
);

const resolveSectionTitle = (section: string) =>
    SECTION_ALIASES[normalizeValue(section)] ?? section;

const orderPermissionEntries = (entries: PermissionCatalogEntry[]) =>
    [...entries].sort((left, right) => {
        const leftIndex = PERMISSION_ORDER_INDEX.get(left.code) ?? Number.MAX_SAFE_INTEGER;
        const rightIndex = PERMISSION_ORDER_INDEX.get(right.code) ?? Number.MAX_SAFE_INTEGER;

        if (leftIndex !== rightIndex) {
            return leftIndex - rightIndex;
        }

        return left.label.localeCompare(right.label);
    });

const orderPermissionCodes = (permissions: readonly string[]) =>
    [...permissions].sort((left, right) => {
        const leftIndex = PERMISSION_ORDER_INDEX.get(left) ?? Number.MAX_SAFE_INTEGER;
        const rightIndex = PERMISSION_ORDER_INDEX.get(right) ?? Number.MAX_SAFE_INTEGER;

        if (leftIndex !== rightIndex) {
            return leftIndex - rightIndex;
        }

        return left.localeCompare(right);
    });

const buildOrderedSections = (catalog: PermissionCatalog): OrderedPermissionSection[] =>
    Object.entries(catalog)
        .map(([section, entries]) => ({
            key: section,
            title: resolveSectionTitle(section),
            entries: orderPermissionEntries(entries),
        }))
        .sort((left, right) => {
            const leftIndex =
                SECTION_ORDER_INDEX.get(left.title as (typeof SECTION_ORDER)[number]) ??
                Number.MAX_SAFE_INTEGER;
            const rightIndex =
                SECTION_ORDER_INDEX.get(right.title as (typeof SECTION_ORDER)[number]) ??
                Number.MAX_SAFE_INTEGER;

            if (leftIndex !== rightIndex) {
                return leftIndex - rightIndex;
            }

            return left.title.localeCompare(right.title);
        });

const resolvePermissionLabel = (
    permission: string,
    catalog?: PermissionCatalog | null
) => {
    if (!catalog) {
        return formatPermissionCode(permission);
    }

    for (const entries of Object.values(catalog)) {
        const match = entries.find((entry) => entry.code === permission);
        if (match) {
            return match.label;
        }
    }

    return formatPermissionCode(permission);
};

const SectionHeader = ({
    title,
    selectedCount,
    totalCount,
    onToggleSection,
    disabled,
}: {
    title: string;
    selectedCount: number;
    totalCount: number;
    onToggleSection: () => void;
    disabled?: boolean;
}) => (
    <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:flex-row sm:items-start sm:justify-between">
        <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {selectedCount} of {totalCount} selected
            </p>
        </div>
        <button
            type="button"
            onClick={onToggleSection}
            disabled={disabled}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-primary-700 dark:hover:text-primary-400"
        >
            {selectedCount === totalCount ? "Clear Section" : "Select Section"}
        </button>
    </div>
);

const PermissionCheckbox = ({
    entry,
    checked,
    onToggle,
    disabled,
}: {
    entry: PermissionCatalogEntry;
    checked: boolean;
    onToggle: () => void;
    disabled?: boolean;
}) => (
    <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={`flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition ${
            checked
                ? "border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20"
                : "border-slate-200 bg-white hover:border-primary-200 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-slate-700 dark:hover:bg-slate-900"
        } disabled:cursor-not-allowed disabled:opacity-60`}
    >
        <span
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                checked
                    ? "border-primary-500 bg-primary-500 text-white"
                    : "border-slate-300 bg-white text-transparent dark:border-slate-600 dark:bg-slate-900"
            }`}
        >
            <CheckIcon className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-start justify-between gap-2">
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-900 dark:text-white">
                        {entry.label}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {entry.description}
                    </span>
                </span>
                <span className="inline-flex shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    {entry.code}
                </span>
            </span>
        </span>
    </button>
);

export const PermissionCatalogEditor = ({
    catalog,
    selectedPermissions,
    onChange,
    disabled,
    hideIntro = false,
    layout = "grid",
}: PermissionCatalogEditorProps) => {
    const [activeSectionKey, setActiveSectionKey] = useState<string>();
    const [searchTerm, setSearchTerm] = useState("");
    const selectedSet = useMemo(() => new Set(selectedPermissions), [selectedPermissions]);
    const groupedEntries = useMemo(() => buildOrderedSections(catalog), [catalog]);

    const activeSection =
        groupedEntries.find((section) => section.key === activeSectionKey) ?? groupedEntries[0];

    const filteredActiveEntries = useMemo(() => {
        if (!activeSection || !searchTerm.trim()) return activeSection?.entries ?? [];

        const query = searchTerm.trim().toLowerCase();
        return activeSection.entries.filter((entry) =>
            `${entry.label} ${entry.description} ${entry.code}`.toLowerCase().includes(query)
        );
    }, [activeSection, searchTerm]);

    const allCodes = useMemo(
        () => groupedEntries.flatMap((section) => section.entries.map((entry) => entry.code)),
        [groupedEntries]
    );

    const handleTogglePermission = (permission: PermissionCode) => {
        const next = new Set(selectedPermissions);
        if (next.has(permission)) {
            next.delete(permission);
        } else {
            next.add(permission);
        }
        onChange(Array.from(next));
    };

    const handleSelectAll = () => onChange(orderPermissionCodes(allCodes) as PermissionCode[]);
    const handleClearAll = () => onChange([]);

    const handleToggleSection = (entries: PermissionCatalogEntry[]) => {
        const next = new Set(selectedPermissions);
        const sectionCodes = entries.map((entry) => entry.code);
        const allSelected = sectionCodes.every((code) => next.has(code));

        sectionCodes.forEach((code) => {
            if (allSelected) {
                next.delete(code);
            } else {
                next.add(code);
            }
        });

        onChange(orderPermissionCodes(Array.from(next)) as PermissionCode[]);
    };

    return (
        <div className="space-y-4">
            <div className={`flex flex-col gap-4 ${hideIntro ? "border-b border-slate-200 pb-4 dark:border-slate-800" : "rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/50"} lg:flex-row lg:items-center lg:justify-between`}>
                <div className="flex items-start gap-3">
                    {!hideIntro ? (
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30">
                            <ShieldCheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                        </div>
                    ) : null}
                    <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">
                            {selectedPermissions.length} permission{selectedPermissions.length === 1 ? "" : "s"} selected
                        </p>
                        <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-500 dark:text-slate-400">
                            {hideIntro ? "Select permissions by capability area. Access is enforced by the backend." : "Choose the exact capabilities this admin should have. Runtime access is always enforced from effective permissions."}
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleSelectAll}
                        disabled={disabled}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-primary-700 dark:hover:text-primary-400"
                    >
                        Select All
                    </button>
                    <button
                        type="button"
                        onClick={handleClearAll}
                        disabled={disabled}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-rose-300 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-rose-800 dark:hover:text-rose-400"
                    >
                        Clear All
                    </button>
                </div>
            </div>

            {layout === "workspace" ? (
                <div className="grid items-start gap-5 lg:grid-cols-[230px_minmax(0,1fr)]">
                    <nav className="rounded-2xl border border-slate-200 bg-slate-50 p-2 dark:border-slate-800 dark:bg-slate-900/60">
                        <p className="px-3 pb-2 pt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
                            Capability areas
                        </p>
                        <div className="grid gap-1 sm:grid-cols-2 lg:block">
                            {groupedEntries.map((section) => {
                                const selectedCount = section.entries.filter((entry) => selectedSet.has(entry.code)).length;
                                const isActive = activeSection?.key === section.key;

                                return (
                                    <button
                                        key={section.key}
                                        type="button"
                                        onClick={() => {
                                            setActiveSectionKey(section.key);
                                            setSearchTerm("");
                                        }}
                                        className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left transition ${
                                            isActive
                                                ? "bg-white text-primary-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:text-primary-300 dark:ring-slate-700"
                                                : "text-slate-600 hover:bg-white/70 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/70 dark:hover:text-slate-200"
                                        }`}
                                    >
                                        <span className="flex min-w-0 items-center gap-2">
                                            {isActive ? <ChevronRightIcon className="h-4 w-4 shrink-0" /> : <span className="h-4 w-4 shrink-0" />}
                                            <span className="truncate text-xs font-medium">{section.title}</span>
                                        </span>
                                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${selectedCount ? "bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300" : "bg-slate-200/80 text-slate-500 dark:bg-slate-700 dark:text-slate-400"}`}>
                                            {selectedCount}/{section.entries.length}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </nav>

                    {activeSection ? (
                        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                            <SectionHeader
                                title={activeSection.title}
                                selectedCount={activeSection.entries.filter((entry) => selectedSet.has(entry.code)).length}
                                totalCount={activeSection.entries.length}
                                onToggleSection={() => handleToggleSection(activeSection.entries)}
                                disabled={disabled}
                            />
                            <div className="border-b border-slate-100 bg-slate-50/60 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/60">
                                <label className="relative block">
                                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="search"
                                        value={searchTerm}
                                        onChange={(event) => setSearchTerm(event.target.value)}
                                        placeholder={`Search ${activeSection.title.toLowerCase()} permissions`}
                                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                                    />
                                </label>
                            </div>
                            <div className="grid gap-3 p-4 xl:grid-cols-2">
                                {filteredActiveEntries.length ? (
                                    filteredActiveEntries.map((entry) => (
                                        <PermissionCheckbox
                                            key={entry.code}
                                            entry={entry}
                                            checked={selectedSet.has(entry.code)}
                                            onToggle={() => handleTogglePermission(entry.code)}
                                            disabled={disabled}
                                        />
                                    ))
                                ) : (
                                    <p className="col-span-full rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                                        No permissions match your search.
                                    </p>
                                )}
                            </div>
                        </section>
                    ) : (
                        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                            No permissions are available.
                        </div>
                    )}
                </div>
            ) : (
                <div className="grid items-start gap-4 xl:grid-cols-2 2xl:grid-cols-3">
                    {groupedEntries.map((section) => {
                        const entries = section.entries;
                        const selectedCount = entries.filter((entry) => selectedSet.has(entry.code)).length;

                        return (
                            <section
                                key={section.key}
                                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
                            >
                                <SectionHeader
                                    title={section.title}
                                    selectedCount={selectedCount}
                                    totalCount={entries.length}
                                    onToggleSection={() => handleToggleSection(entries)}
                                    disabled={disabled}
                                />
                                <div className="max-h-80 space-y-2 overflow-y-auto p-3">
                                    {entries.map((entry) => (
                                        <PermissionCheckbox
                                            key={entry.code}
                                            entry={entry}
                                            checked={selectedSet.has(entry.code)}
                                            onToggle={() => handleTogglePermission(entry.code)}
                                            disabled={disabled}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const PermissionSummary = ({
    permissions,
    catalog,
    emptyLabel = "No explicit permissions assigned",
    maxVisible = 8,
}: PermissionSummaryProps) => {
    const normalizedPermissions = orderPermissionCodes(permissions ?? []);

    if (normalizedPermissions.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-400">
                {emptyLabel}
            </div>
        );
    }

    const visiblePermissions = normalizedPermissions.slice(0, maxVisible);
    const remainingCount = normalizedPermissions.length - visiblePermissions.length;

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <Squares2X2Icon className="h-4 w-4" />
                {normalizedPermissions.length} permissions
            </div>
            <div className="flex flex-wrap gap-2">
                {visiblePermissions.map((permission) => (
                    <span
                        key={permission}
                        className="inline-flex rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                    >
                        {resolvePermissionLabel(permission, catalog)}
                    </span>
                ))}
                {remainingCount > 0 ? (
                    <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        +{remainingCount} more
                    </span>
                ) : null}
            </div>
        </div>
    );
};
